import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supplierService } from '../../services/supplierService';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MetricCard from './components/MetricCard';
import AddressManagementSection from '../customer-profile-edit/components/AddressManagementSection';
import RevenueChart from './components/RevenueChart';
import ActiveOrderCard from './components/ActiveOrderCard';
import TierProgressCard from './components/TierProgressCard';
import MenuItemCard from './components/MenuItemCard';

import CustomerRetentionCard from './components/CustomerRetentionCard';
import StatusUpdateModal from './components/StatusUpdateModal';
import Image from '../../components/AppImage';


const SupplierDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [chartType, setChartType] = useState('line');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [supplierProfile, setSupplierProfile] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Profile edit state
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileFormData, setProfileFormData] = useState({ business_name: '', service_description: '', country: '', profile_image: '' });
  const [addressesState, setAddressesState] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);

  // Add menu item modal state
  const [showAddMenuModal, setShowAddMenuModal] = useState(false);
  const [showEditMenuModal, setShowEditMenuModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [menuFormData, setMenuFormData] = useState({
    name: '',
    description: '',
    price_per_plate: '',
    cuisine: '',
    dietary_tags: [],
    image: '',
    is_available: true
  });
  const [menuFormLoading, setMenuFormLoading] = useState(false);

  // Add this block - declare missing state for new order alert
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  
  // Add status update modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedOrderForUpdate, setSelectedOrderForUpdate] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  // Add this block - declare missing state for order details modal
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);
  // End of added block

  useEffect(() => {
    if (authLoading) return; // Wait for auth state to finish loading
    if (!user) {
      navigate('/login');
      return;
    }

    const qs = new URLSearchParams(location?.search);
    const editParam = qs.get('editProfile') === 'true';
    setShowProfileEdit(editParam);

    loadDashboardData();
  }, [user, authLoading, location?.search]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get supplier profile
      const { data: supplier, error: supplierError } = await supplierService?.getSupplierByUserId(user?.id);
      if (supplierError) throw supplierError;
      setSupplierProfile(supplier);

      // Prefill profile form and address if present
      setProfileFormData({
        business_name: supplier?.business_name || '',
        service_description: supplier?.service_description || '',
        country: supplier?.country || '',
        profile_image: supplier?.profile_image || ''
      });
      setAddressesState((supplier?.addresses || []).map(a => ({
        id: a?.id,
        label: a?.label || '',
        locality: a?.locality ?? a?.street ?? '',
        city: a?.city ?? '',
        state: a?.state ?? '',
        pinCode: a?.pin_code ?? a?.pinCode ?? a?.zipCode ?? '',
        country: a?.country ?? '',
        isPrimary: a?.is_primary ?? a?.isPrimary ?? false
      })));


      // Get revenue data
      const { data: revenue } = await supplierService?.getRevenueData(supplier?.id);
      setRevenueData(revenue || []);

      // Get orders
      const { data: orders } = await supplierService?.getOrders(supplier?.id);
      // Debug log: inspect the first order's customer and user structure
      if (orders && orders.length > 0) {
        // Log the first order's customer and user structure
        // eslint-disable-next-line no-console
        console.log('DEBUG: First order object:', orders[0]);
        // eslint-disable-next-line no-console
        console.log('DEBUG: orders[0].customer:', orders[0]?.customer);
        // eslint-disable-next-line no-console
        console.log('DEBUG: orders[0].customer.user:', orders[0]?.customer?.user);
        
        console.log('DEBUG: orders[0].customer.user:', orders[0]?.userProfile);
      }
      
      // Transform orders to match ActiveOrderCard expected format
      const transformedOrders = orders?.map(order => ({
        orderId: order?.order_number || order?.id,
        customerName: order?.customer?.user?.full_name || order?.customer?.full_name || 'Guest Customer',
        customerImage: order?.customer?.user?.avatar_url || order?.customer?.avatar_url || 'https://via.placeholder.com/150',
        customerImageAlt: `Profile picture of ${order?.customer?.user?.full_name || order?.customer?.full_name || 'customer'}`,
        status: order?.status,
        timestamp: order?.created_at,
        deliveryType: order?.delivery_type || 'delivery',
        totalAmount: parseFloat(order?.total_amount || 0)?.toFixed(2),
        specialInstructions: order?.special_instructions || '',
        deliveryAddress: order?.delivery_address || '',
        deliveryTime: order?.delivery_time || '',
        items: order?.order_items?.map(item => ({
          name: item?.menu_item?.name || 'Unknown Item',
          quantity: item?.quantity,
          price: parseFloat(item?.price || 0)?.toFixed(2),
          image: item?.menu_item?.image || 'https://via.placeholder.com/150',
          imageAlt: `Image of ${item?.menu_item?.name || 'menu item'}`
        })) || []
      })) || [];
      
      setActiveOrders(transformedOrders);

      // Get menu items
      const { data: menu } = await supplierService?.getMenuItems(supplier?.id);
      setMenuItems(menu || []);

      // Get notifications
      const { data: notifs } = await supplierService?.getNotifications(user?.id);
      setNotifications(notifs || []);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderStatusUpdate = async (orderId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await supplierService?.updateOrderStatus(orderId, newStatus);
      // Reload orders
      const { data: orders } = await supplierService?.getOrders(supplierProfile?.id);
      
      // Transform orders again
      const transformedOrders = orders?.map(order => ({
        orderId: order?.order_number || order?.id,
        customerName: order?.customer?.user?.full_name || order?.customer?.full_name || 'Guest Customer',
        customerImage: order?.customer?.user?.avatar_url || order?.customer?.avatar_url || 'https://via.placeholder.com/150',
        customerImageAlt: `Profile picture of ${order?.customer?.user?.full_name || order?.customer?.full_name || 'customer'}`,
        status: order?.status,
        timestamp: order?.created_at,
        deliveryType: order?.delivery_type || 'delivery',
        totalAmount: parseFloat(order?.total_amount || 0)?.toFixed(2),
        specialInstructions: order?.special_instructions || '',
        deliveryAddress: order?.delivery_address || '',
        deliveryTime: order?.delivery_time || '',
        items: order?.order_items?.map(item => ({
          name: item?.menu_item?.name || 'Unknown Item',
          quantity: item?.quantity,
          price: parseFloat(item?.price || 0)?.toFixed(2),
          image: item?.menu_item?.image || 'https://via.placeholder.com/150',
          imageAlt: `Image of ${item?.menu_item?.name || 'menu item'}`
        })) || []
      })) || [];
      
      setActiveOrders(transformedOrders);
      setShowStatusModal(false);
      setSelectedOrderForUpdate(null);
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Failed to update order status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Calculate metrics from real data
  const metrics = [
    {
      title: 'Total Revenue',
      value: `$${supplierProfile?.total_orders ? (supplierProfile?.total_orders * 15)?.toFixed(2) : '0.00'}`,
      change: '+23.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      iconColor: 'var(--color-success)',
      trend: 'vs last month'
    },
    {
      title: 'Active Orders',
      value: (activeOrders?.filter(o => ['pending', 'preparing']?.includes(o?.status))?.length)?.toString() || '0',
      change: `+${activeOrders?.filter(o => o?.status === 'pending')?.length || 0}`,
      changeType: 'positive',
      icon: 'ShoppingBag',
      iconColor: 'var(--color-primary)',
      trend: 'pending'
    },
    {
      title: 'Total Orders',
      value: supplierProfile?.total_orders?.toString() || '0',
      change: '+18.2%',
      changeType: 'positive',
      icon: 'TrendingUp',
      iconColor: 'var(--color-accent)',
      trend: 'this month'
    },
    {
      title: 'Customer Rating',
      value: supplierProfile?.rating ? supplierProfile.rating.toFixed(1) : '0.0',
      change: '+0.3',
      changeType: 'positive',
      icon: 'Star',
      iconColor: 'var(--color-warning)',
      trend: 'average'
    }
  ];

  // Save profile handler
  const handleSaveProfile = async (e) => {
    e?.preventDefault();
    if (!supplierProfile?.id) return;
    setSavingProfile(true);
    try {
      const { data: updatedSupplier, error: updErr } = await supplierService.updateSupplier(supplierProfile?.id, {
        business_name: profileFormData.business_name,
        service_description: profileFormData.service_description,
        country: profileFormData.country,
        profile_image: profileFormData.profile_image
      });
      if (updErr) throw updErr;

      // Ensure each address has user_id set
      const addressesWithUserId = addressesState.map(a => ({ ...a, user_id: supplierProfile?.user_id }));
      const { data: addrData, error: addrErr } = await supplierService.updateSupplierAddresses(supplierProfile?.id, addressesWithUserId);
      if (addrErr) throw addrErr;

      // Update supplier core profile
      setShowProfileEdit(false);
      await loadDashboardData();
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError(err?.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setShowProfileEdit(false);
  };

  const tierData = {
    currentTier: 'Silver',
    nextTier: 'Gold',
    currentRevenue: supplierProfile?.total_orders ? supplierProfile?.total_orders * 15 : 0,
    nextTierThreshold: 75000,
    orderCount: supplierProfile?.total_orders || 0,
    benefits: [
      'Reduced platform commission (12% → 10%)',
      'Featured placement in search results',
      'Priority customer support',
      'Advanced analytics dashboard',
      'Custom branding options'
    ]
  };

  const customerRetentionData = {
    newCustomers: 45,
    returningCustomers: 128,
    loyalCustomers: 67,
    totalCustomers: 240,
    retentionRate: 81.3
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Profile edit UI when requested
  const ProfileEditPanel = () => (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <h3 className="font-heading font-bold text-lg mb-4">Edit Kitchen Profile</h3>
      <form onSubmit={handleSaveProfile} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Business Name</label>
          <input value={profileFormData.business_name} onChange={(e) => setProfileFormData(p => ({ ...p, business_name: e.target.value }))} className="w-full px-4 py-2 border rounded-md" />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Country</label>
          <input value={profileFormData.country} onChange={(e) => setProfileFormData(p => ({ ...p, country: e.target.value }))} className="w-full px-4 py-2 border rounded-md" />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm text-muted-foreground mb-2 block">About / Service Description</label>
          <textarea value={profileFormData.service_description} onChange={(e) => setProfileFormData(p => ({ ...p, service_description: e.target.value }))} className="w-full px-4 py-2 border rounded-md" rows={4} />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm text-muted-foreground mb-2 block">Profile Image URL</label>
          <input
            type="url"
            value={profileFormData.profile_image}
            onChange={(e) => setProfileFormData(p => ({ ...p, profile_image: e.target.value }))}
            className="w-full px-4 py-2 border rounded-md"
            placeholder="https://example.com/image.jpg"
          />
        </div>
        <div className="md:col-span-2 mt-2">
          <h4 className="font-semibold mb-2">Addresses</h4>
          <AddressManagementSection profile={{ addresses: addressesState }} onSave={({ addresses }) => setAddressesState(addresses)} saving={savingProfile} />
        </div>
        <div className="md:col-span-2 flex gap-3 justify-end mt-4">
          <Button variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
          <Button variant="default" type="submit" loading={savingProfile}>Save Profile</Button>
        </div>
      </form>
    </div>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={loadDashboardData}>Retry</Button>
        </div>
      </div>
    );
  }

  const handleUpdateStatus = (orderId) => {
    const order = activeOrders?.find(o => o?.orderId === orderId);
    if (order) {
      setSelectedOrderForUpdate(order);
      setShowStatusModal(true);
    }
  };

  const handleViewDetails = (orderId) => {
    const order = activeOrders?.find(o => o?.orderId === orderId);
    if (order) {
      setSelectedOrderForDetails(order);
      setShowOrderDetailsModal(true);
    }
  };

  const handleEditMenuItem = (itemId) => {
    const item = menuItems?.find(m => m?.id === itemId);
    if (!item) return;
    
    setEditingItemId(itemId);
    setMenuFormData({
      name: item?.name || '',
      description: item?.description || '',
      price_per_plate: item?.price_per_plate?.toString() || '',
      cuisine: item?.cuisine || '',
      dietary_tags: item?.dietary_tags || [],
      image: item?.image || '',
      is_available: item?.is_available ?? true
    });
    setShowEditMenuModal(true);
  };

  const handleToggleAvailability = (itemId) => {
    console.log('Toggle availability for item:', itemId);
  };

  const handleDeleteMenuItem = (itemId) => {
    console.log('Delete menu item:', itemId);
  };

  const handleOpenAddMenuModal = () => {
    setShowAddMenuModal(true);
    setMenuFormData({
      name: '',
      description: '',
      price_per_plate: '',
      cuisine: '',
      dietary_tags: [],
      image: '',
      is_available: true
    });
  };

  const handleCloseAddMenuModal = () => {
    setShowAddMenuModal(false);
    setError(null);
    setMenuFormData({
      name: '',
      description: '',
      price_per_plate: '',
      cuisine: '',
      dietary_tags: [],
      image: '',
      is_available: true
    });
  };

  const handleCloseEditMenuModal = () => {
    setShowEditMenuModal(false);
    setEditingItemId(null);
    setError(null);
    setMenuFormData({
      name: '',
      description: '',
      price_per_plate: '',
      cuisine: '',
      dietary_tags: [],
      image: '',
      is_available: true
    });
  };

  const handleMenuFormChange = (field, value) => {
    setMenuFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddMenuItem = async (e) => {
    e?.preventDefault();
    
    if (!supplierProfile?.id) {
      setError('Supplier profile not found');
      return;
    }

    setMenuFormLoading(true);
    try {
      const menuItemData = {
        supplier_id: supplierProfile?.id,
        name: menuFormData?.name,
        description: menuFormData?.description,
        price_per_plate: parseFloat(menuFormData?.price_per_plate),
        cuisine: menuFormData?.cuisine,
        dietary_tags: menuFormData?.dietary_tags,
        image: menuFormData?.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800',
        is_available: menuFormData?.is_available
      };

      const { data, error: createError } = await supplierService?.createMenuItem(menuItemData);
      
      if (createError) throw createError;

      // Reload menu items
      const { data: updatedMenu } = await supplierService?.getMenuItems(supplierProfile?.id);
      setMenuItems(updatedMenu || []);

      handleCloseAddMenuModal();
    } catch (err) {
      setError(err?.message || 'Failed to add menu item');
    } finally {
      setMenuFormLoading(false);
    }
  };

  const handleUpdateMenuItem = async (e) => {
    e?.preventDefault();
    
    if (!editingItemId) {
      setError('No item selected for editing');
      return;
    }

    setMenuFormLoading(true);
    try {
      const updates = {
        name: menuFormData?.name,
        description: menuFormData?.description,
        price_per_plate: parseFloat(menuFormData?.price_per_plate),
        cuisine: menuFormData?.cuisine,
        dietary_tags: menuFormData?.dietary_tags,
        image: menuFormData?.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800',
        is_available: menuFormData?.is_available
      };

      const { data, error: updateError } = await supplierService?.updateMenuItem(editingItemId, updates);
      
      if (updateError) throw updateError;

      // Reload menu items
      const { data: updatedMenu } = await supplierService?.getMenuItems(supplierProfile?.id);
      setMenuItems(updatedMenu || []);

      handleCloseEditMenuModal();
    } catch (err) {
      setError(err?.message || 'Failed to update menu item');
    } finally {
      setMenuFormLoading(false);
    }
  };

  const handleOpenChat = (chatId) => {
    console.log('Open chat:', chatId);
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Mark as read:', notificationId);
  };

  const handleMarkAllAsRead = () => {
    console.log('Mark all as read');
  };

  // Only show 'orders' tab if not a supplier
  const isSupplier = userProfile?.role === 'supplier';
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    // Hide 'orders' tab for suppliers
    ...(!isSupplier ? [{ id: 'orders', label: 'Active Orders', icon: 'ShoppingBag' }] : []),
    { id: 'menu', label: 'Menu Management', icon: 'ChefHat' },
    { id: 'chats', label: 'Customer Chats', icon: 'MessageSquare' }
  ];


  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2">
              Supplier Dashboard
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Manage your kitchen, orders, and customer relationships
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => console.log('Open calendar')}>

              Schedule
            </Button>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={handleOpenAddMenuModal}>

              Add Menu Item
            </Button>
          </div>
        </div>

        <div className="mb-6 md:mb-8 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {tabs?.map((tab) =>
            <button
              key={tab?.id}
              onClick={() => setSelectedTab(tab?.id)}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-lg font-medium transition-smooth whitespace-nowrap ${
              selectedTab === tab?.id ?
              'bg-primary text-primary-foreground' :
              'bg-card text-foreground hover:bg-muted'}`
              }>

                <Icon name={tab?.icon} size={20} />
                <span className="text-sm md:text-base">{tab?.label}</span>
              </button>
            )}
          </div>
        </div>

        {selectedTab === 'overview' && (
        <div className="space-y-6 md:space-y-8">
            {showProfileEdit && <ProfileEditPanel />}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {metrics?.map((metric, index) =>
            <MetricCard key={index} {...metric} />
            )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2">
                <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                      Revenue Analytics
                    </h2>
                    <div className="flex gap-2">
                      <button
                      onClick={() => setChartType('line')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                      chartType === 'line' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
                      }>

                        Line
                      </button>
                      <button
                      onClick={() => setChartType('bar')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                      chartType === 'bar' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
                      }>

                        Bar
                      </button>
                    </div>
                  </div>
                  <RevenueChart data={revenueData} chartType={chartType} />
                </div>
              </div>

              <div>
                <TierProgressCard {...tierData} />
              </div>
            </div>

            <CustomerRetentionCard data={customerRetentionData} />

            <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                  Recent Orders
                </h2>
                <Button
                variant="ghost"
                size="sm"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => setSelectedTab('orders')}>

                  View All
                </Button>
              </div>
              <div className="space-y-4">
                {activeOrders?.slice(0, 2)?.map((order) =>
              <ActiveOrderCard
                key={order?.orderId}
                order={order}
                onUpdateStatus={handleUpdateStatus}
                onViewDetails={handleViewDetails} />

              )}
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'orders' && (
        <div className="space-y-4 md:space-y-6">
            <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-6">
                Active Orders ({activeOrders?.length})
              </h2>
              <div className="space-y-4">
                {activeOrders?.map((order) =>
              <ActiveOrderCard
                key={order?.orderId}
                order={order}
                onUpdateStatus={handleUpdateStatus}
                onViewDetails={handleViewDetails} />

              )}
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'menu' && (
        <div className="space-y-6 md:space-y-8">
            <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                  Menu Items ({menuItems?.length})
                </h2>
                <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleOpenAddMenuModal}>

                  Add New Item
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {menuItems?.map((item) =>
              <MenuItemCard
                key={item?.id}
                item={item}
                onEdit={handleEditMenuItem}
                onToggleAvailability={handleToggleAvailability}
                onDelete={handleDeleteMenuItem} />

              )}
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'chats' && (
        <div className="space-y-6 md:space-y-8">
            <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                  Customer Conversations
                </h2>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 rounded-lg">
                  <Icon name="MessageSquare" size={16} color="var(--color-accent)" />
                  <span className="text-sm font-semibold text-accent data-text">
                    {notifications?.filter((n) => !n?.read)?.length} unread
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {notifications?.slice(0, 3)?.map((notification) =>
              <div key={notification?.id} className={`rounded-lg border border-accent/10 p-4 bg-card ${notification?.read ? '' : 'ring-2 ring-primary'}`}>
                <div className="flex items-center gap-2">
                  <Icon name={notification?.type === 'order' ? 'ShoppingBag' : 'MessageSquare'} size={16} color="var(--color-accent)" />
                  <div className="font-medium text-foreground">{notification?.title}</div>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {notification?.message}
                </p>
                <div className="text-xs text-muted-foreground mt-2">
                  {new Date(notification?.timestamp)?.toLocaleDateString()}
                </div>
              </div>

              )}
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl shadow-warm p-6 md:p-8 text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MessageCircle" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2">
                Real-Time Customer Communication
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
                Chat directly with customers to discuss custom orders, share inspiration photos, and provide personalized service for special occasions like birthday cakes.
              </p>
              <Button
              variant="default"
              size="lg"
              iconName="MessageSquare"
              iconPosition="left">

                Start New Conversation
              </Button>
            </div>
          </div>
        )}

        </div>
        {/* New Order Alert Notification */}
      {newOrderAlert && (
        <div className="fixed top-20 right-4 z-50 max-w-md animate-slide-in-right">
          <div className="bg-white rounded-lg shadow-2xl border-2 border-primary overflow-hidden">
            <div className="bg-primary px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Bell" size={20} className="text-white animate-bounce" />
                <h3 className="text-white font-semibold text-lg">New Order Received!</h3>
              </div>
              <button
                onClick={() => setNewOrderAlert(null)}
                className="text-white hover:bg-white/20 rounded p-1 transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            
            <div className="p-4 space-y-3">
              {/* Customer Info */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    {newOrderAlert?.customer?.user?.full_name || 'Customer'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {newOrderAlert?.customer?.user?.phone || 'No phone'}
                  </p>
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Icon name="ShoppingBag" size={16} className="text-primary" />
                  Order Items ({newOrderAlert?.order_items?.length || 0})
                </p>
                {newOrderAlert?.order_items?.slice(0, 3)?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item?.quantity}x {item?.menu_item?.name}
                    </span>
                    <span className="font-medium text-foreground">
                      ${(item?.quantity * item?.menu_item?.price_per_plate)?.toFixed(2)}
                    </span>
                  </div>
                ))}
                {newOrderAlert?.order_items?.length > 3 && (
                  <p className="text-xs text-muted-foreground italic">
                    +{newOrderAlert?.order_items?.length - 3} more items
                  </p>
                )}
              </div>

              {/* Delivery Details */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Icon name="MapPin" size={16} className="text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-muted-foreground">Delivery Address</p>
                    <p className="text-sm text-foreground">
                      {newOrderAlert?.delivery_address || 'Pickup'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Icon name="Clock" size={16} className="text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-muted-foreground">Estimated Delivery</p>
                    <p className="text-sm text-foreground">
                      {newOrderAlert?.estimated_delivery
                        ? new Date(newOrderAlert?.estimated_delivery)?.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })
                        : 'ASAP'}
                    </p>
                  </div>
                </div>

                {/* Special Instructions */}
                {newOrderAlert?.special_instructions && (
                  <div className="flex items-start gap-2">
                    <Icon name="FileText" size={16} className="text-primary mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted-foreground">Special Instructions</p>
                      <p className="text-sm text-foreground italic">
                        "{newOrderAlert?.special_instructions}"
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Total Amount */}
              <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">Total Amount</span>
                <span className="text-xl font-bold text-primary">
                  ${newOrderAlert?.total_amount?.toFixed(2)}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => {
                    setSelectedTab('orders');
                    setNewOrderAlert(null);
                  }}
                  className="flex-1"
                  size="sm"
                >
                  <Icon name="Eye" size={16} className="mr-1" />
                  View Order
                </Button>
                <Button
                  onClick={() => {
                    handleOrderStatusUpdate(newOrderAlert?.id, 'preparing');
                    setNewOrderAlert(null);
                  }}
                  variant="outline"
                  className="flex-1"
                  size="sm"
                >
                  <Icon name="CheckCircle" size={16} className="mr-1" />
                  Accept
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Menu Item Modal */}
      {showAddMenuModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-warm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                Add New Menu Item
              </h2>
              <button
                onClick={handleCloseAddMenuModal}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleAddMenuItem} className="p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dish Name *
                </label>
                <input
                  type="text"
                  required
                  value={menuFormData?.name}
                  onChange={(e) => handleMenuFormChange('name', e?.target?.value)}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Butter Chicken"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description *
                </label>
                <textarea
                  required
                  value={menuFormData?.description}
                  onChange={(e) => handleMenuFormChange('description', e?.target?.value)}
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Describe your dish..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Price per Plate ($) *
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    value={menuFormData?.price_per_plate}
                    onChange={(e) => handleMenuFormChange('price_per_plate', e?.target?.value)}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="15.99"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Cuisine Type *
                  </label>
                  <select
                    required
                    value={menuFormData?.cuisine}
                    onChange={(e) => handleMenuFormChange('cuisine', e?.target?.value)}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select cuisine</option>
                    <option value="Indian">Indian</option>
                    <option value="Chinese">Chinese</option>
                    <option value="Italian">Italian</option>
                    <option value="Mexican">Mexican</option>
                    <option value="Thai">Thai</option>
                    <option value="American">American</option>
                    <option value="Mediterranean">Mediterranean</option>
                    <option value="Japanese">Japanese</option>
                    <option value="Cake">Cake</option>
                    <option value="Sweets">Sweets</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  value={menuFormData?.image}
                  onChange={(e) => handleMenuFormChange('image', e?.target?.value)}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dietary Information
                </label>
                <div className="flex flex-wrap gap-2">
                  {['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Halal', 'Kosher']?.map((diet) => (
                    <button
                      key={diet}
                      type="button"
                      onClick={() => {
                        const current = menuFormData?.dietary_tags || [];
                        if (current?.includes(diet)) {
                          handleMenuFormChange('dietary_tags', current?.filter(d => d !== diet));
                        } else {
                          handleMenuFormChange('dietary_tags', [...current, diet]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth ${
                        menuFormData?.dietary_tags?.includes(diet)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    >
                      {diet}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_available"
                  checked={menuFormData?.is_available}
                  onChange={(e) => handleMenuFormChange('is_available', e?.target?.checked)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="is_available" className="text-sm font-medium text-foreground">
                  Available for orders
                </label>
              </div>

              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={handleCloseAddMenuModal}
                  disabled={menuFormLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  disabled={menuFormLoading}
                  iconName={menuFormLoading ? 'Loader2' : 'Plus'}
                  iconPosition="left"
                >
                  {menuFormLoading ? 'Adding...' : 'Add Menu Item'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Menu Item Modal */}
      {showEditMenuModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-warm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                Edit Menu Item
              </h2>
              <button
                onClick={handleCloseEditMenuModal}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdateMenuItem} className="p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dish Name *
                </label>
                <input
                  type="text"
                  required
                  value={menuFormData?.name}
                  onChange={(e) => handleMenuFormChange('name', e?.target?.value)}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Butter Chicken"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description *
                </label>
                <textarea
                  required
                  value={menuFormData?.description}
                  onChange={(e) => handleMenuFormChange('description', e?.target?.value)}
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Describe your dish..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Price per Plate ($) *
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    value={menuFormData?.price_per_plate}
                    onChange={(e) => handleMenuFormChange('price_per_plate', e?.target?.value)}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="15.99"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Cuisine Type *
                  </label>
                  <select
                    required
                    value={menuFormData?.cuisine}
                    onChange={(e) => handleMenuFormChange('cuisine', e?.target?.value)}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select cuisine</option>
                    <option value="Indian">Indian</option>
                    <option value="Chinese">Chinese</option>
                    <option value="Italian">Italian</option>
                    <option value="Mexican">Mexican</option>
                    <option value="Thai">Thai</option>
                    <option value="American">American</option>
                    <option value="Mediterranean">Mediterranean</option>
                    <option value="Japanese">Japanese</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  value={menuFormData?.image}
                  onChange={(e) => handleMenuFormChange('image', e?.target?.value)}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dietary Information
                </label>
                <div className="flex flex-wrap gap-2">
                  {['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Halal', 'Kosher']?.map((diet) => (
                    <button
                      key={diet}
                      type="button"
                      onClick={() => {
                        const current = menuFormData?.dietary_tags || [];
                        if (current?.includes(diet)) {
                          handleMenuFormChange('dietary_tags', current?.filter(d => d !== diet));
                        } else {
                          handleMenuFormChange('dietary_tags', [...current, diet]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth ${
                        menuFormData?.dietary_tags?.includes(diet)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    >
                      {diet}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_is_available"
                  checked={menuFormData?.is_available}
                  onChange={(e) => handleMenuFormChange('is_available', e?.target?.checked)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="edit_is_available" className="text-sm font-medium text-foreground">
                  Available for orders
                </label>
              </div>

              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={handleCloseEditMenuModal}
                  disabled={menuFormLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  disabled={menuFormLoading}
                  iconName={menuFormLoading ? 'Loader2' : 'Save'}
                  iconPosition="left"
                >
                  {menuFormLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedOrderForUpdate && (
        <StatusUpdateModal
          order={selectedOrderForUpdate}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedOrderForUpdate(null);
          }}
          onUpdateStatus={handleOrderStatusUpdate}
          loading={updatingStatus}
          isUpdating={updatingStatus}
        />
      )}

      {/* Order Details Modal */}
      {showOrderDetailsModal && selectedOrderForDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-warm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
              <h3 className="font-heading font-bold text-xl text-foreground">Order Details</h3>
              <button
                onClick={() => {
                  setShowOrderDetailsModal(false);
                  setSelectedOrderForDetails(null);
                }}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
              >
                <Icon name="X" size={20} color="var(--color-muted-foreground)" />
              </button>
            </div>
            
            <div className="p-4 md:p-6 space-y-6">
              {/* Order Info */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Order ID</span>
                  <span className="font-semibold text-foreground">#{selectedOrderForDetails?.orderId}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Customer</span>
                  <span className="font-semibold text-foreground">{selectedOrderForDetails?.customerName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className="font-semibold text-foreground capitalize">{selectedOrderForDetails?.status?.replace('_', ' ')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Amount</span>
                  <span className="font-semibold text-foreground data-text">${Number(selectedOrderForDetails?.totalAmount ?? 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h4 className="font-heading font-semibold text-lg text-foreground mb-3">Order Items</h4>
                <div className="space-y-3">
                  {selectedOrderForDetails?.items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <Image
                        src={item?.image}
                        alt={item?.imageAlt}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{item?.name}</p>
                        <p className="text-sm text-muted-foreground">Quantity: {item?.quantity}</p>
                      </div>
                      <span className="font-semibold text-foreground data-text">${Number(item?.price_per_plate ?? item?.menu_item?.price_per_plate ?? item?.price ?? 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Delivery Info */}
              {selectedOrderForDetails?.deliveryAddress && (
                <div>
                  <h4 className="font-heading font-semibold text-lg text-foreground mb-3">Delivery Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Icon name="MapPin" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-sm text-muted-foreground">Address</p>
                        <p className="text-foreground">{selectedOrderForDetails?.deliveryAddress}</p>
                      </div>
                    </div>
                    {selectedOrderForDetails?.deliveryTime && (
                      <div className="flex items-start gap-2">
                        <Icon name="Clock" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-sm text-muted-foreground">Delivery Time</p>
                          <p className="text-foreground">{selectedOrderForDetails?.deliveryTime}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              {selectedOrderForDetails?.specialInstructions && (
                <div>
                  <h4 className="font-heading font-semibold text-lg text-foreground mb-3">Special Instructions</h4>
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-foreground">{selectedOrderForDetails?.specialInstructions}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierDashboard;